import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronUp, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { PORTFOLIO, WORKSPACE_IMAGE } from "@/data/site";

const DURATION = 6500;

const BRAND_SLIDE = {
  id: "sparkplug",
  title: "Spark Plug",
  headline: ["we ignite", "growth."],
  image: WORKSPACE_IMAGE,
  cta: { label: "Explore", to: "/#services" },
};

const DESIGN_SLIDE = {
  id: "amazing-designs",
  title: "Need Amazing Designs",
  headline: ["need amazing", "designs."],
  description:
    "At Sparkplug we make unique marketing strategies with amazing designs that drive traffic, generate visibility and ignite your brand.",
  image: "/assets/amazing-designs.png",
};

const SLIDES = [BRAND_SLIDE, DESIGN_SLIDE, ...PORTFOLIO.filter((p) => p.id === "visibility")];

const HeroSlider = () => {
  const [[index], setState] = useState([0]);
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const go = useCallback(
    (d) => setState(([i]) => [(i + d + SLIDES.length) % SLIDES.length]),
    []
  );

  useEffect(() => {
    const t = setInterval(() => go(1), DURATION);
    return () => clearInterval(t);
  }, [go, index]);

  const p = SLIDES[index];
  const cta = p.cta || { label: "More", to: "/contact" };

  return (
    <section className="relative" data-testid="hero-slider-section">
      <div
        className="relative h-[100svh] min-h-[560px] w-full overflow-hidden bg-[#131316]"
        data-testid="hero-slider"
      >
        <AnimatePresence initial={false}>
          <motion.div
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 1.06, filter: "brightness(1.7)" }}
            transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1] }}
            className="absolute inset-0"
            data-testid={`hero-slide-${p.id}`}
          >
            <img
              src={p.image}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full scale-125 object-cover opacity-70 blur-2xl sm:hidden"
            />
            <motion.img
              key={`img-${index}`}
              src={p.image}
              alt={p.title}
              initial={{ scale: 1 }}
              animate={{ scale: isDesktop ? 1.12 : 1 }}
              transition={{ duration: DURATION / 1000 + 1.6, ease: "linear" }}
              className="absolute inset-0 h-full w-full object-contain sm:object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#09090B]/90 via-[#09090B]/20 to-[#09090B]/50" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#09090B]/50 via-transparent to-transparent" />
          </motion.div>
        </AnimatePresence>

        <div className="absolute bottom-14 left-4 right-4 z-10 sm:bottom-16 sm:left-10 sm:right-6 sm:max-w-[58%] lg:left-16">
          <AnimatePresence mode="wait">
            <motion.div key={`caption-${index}`}>
              {p.category && (
                <motion.span
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, delay: 0.25 }}
                  className="mono mb-4 block text-[10px] uppercase tracking-[0.25em] text-[#E9C766] sm:text-xs"
                  data-testid="hero-slide-category"
                >
                  {p.category}
                </motion.span>
              )}
              <h1 className="font-display text-[13vw] font-extrabold lowercase leading-[0.98] tracking-tight text-white drop-shadow-2xl sm:text-6xl md:text-7xl lg:text-8xl" data-testid="slider-headline">
                {p.headline.map((line, li) => (
                  <span key={line} className="block overflow-hidden pb-1">
                    <motion.span
                      className="block"
                      initial={{ y: "112%" }}
                      animate={{ y: 0 }}
                      exit={{ y: "-112%" }}
                      transition={{
                        duration: 0.75,
                        delay: 0.3 + li * 0.12,
                        ease: [0.16, 1, 0.3, 1],
                      }}
                    >
                      {line}
                    </motion.span>
                  </span>
                ))}
              </h1>
              {p.metric && (
                <motion.div
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                  className="mt-5 flex flex-wrap items-center gap-3"
                >
                  <span className="font-display text-xl font-extrabold text-[#D9A92E] sm:text-2xl">
                    {p.metric}
                  </span>
                  <span className="text-xs text-white/70 sm:text-sm">{p.metricLabel}</span>
                  <span className="mx-1 hidden h-4 w-px bg-white/25 sm:block" />
                  {p.tags.map((t) => (
                    <span
                      key={t}
                      className="mono hidden rounded-full border border-white/25 bg-[#09090B]/40 px-3 py-1 text-[10px] uppercase tracking-wider text-white backdrop-blur-sm sm:block"
                    >
                      {t}
                    </span>
                  ))}
                </motion.div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="absolute bottom-14 right-4 z-10 hidden max-w-xs sm:bottom-16 sm:right-10 md:block lg:right-16">
          <AnimatePresence mode="wait">
            <motion.div
              key={`desc-${index}`}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, delay: 0.45 }}
            >
              {p.description && (
                <p className="text-sm leading-relaxed text-white/85">{p.description}</p>
              )}
              <Link
                to={cta.to}
                data-testid="hero-more-button"
                className="mt-6 inline-block rounded-full border border-white/60 px-8 py-2.5 text-xs font-semibold uppercase tracking-[0.18em] text-white transition-all duration-300 hover:bg-white hover:text-[#0F1B3D]"
              >
                {cta.label}
              </Link>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="absolute right-4 top-1/2 z-20 flex -translate-y-1/2 flex-col gap-3 sm:right-6">
          <button
            onClick={() => go(-1)}
            aria-label="Previous slide"
            data-testid="hero-prev"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-[#09090B]/30 text-white backdrop-blur-md transition-all duration-300 hover:border-white hover:bg-white hover:text-[#0F1B3D]"
          >
            <ChevronUp className="h-5 w-5" />
          </button>
          <button
            onClick={() => go(1)}
            aria-label="Next slide"
            data-testid="hero-next"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-[#09090B]/30 text-white backdrop-blur-md transition-all duration-300 hover:border-white hover:bg-white hover:text-[#0F1B3D]"
          >
            <ChevronDown className="h-5 w-5" />
          </button>
        </div>

        <div className="absolute left-4 top-24 z-10 sm:left-10 lg:left-16">
          <span className="mono text-xs tracking-[0.25em] text-white/70" data-testid="hero-slide-counter">
            0{index + 1} <span className="text-white/35">/ 0{SLIDES.length}</span>
          </span>
        </div>
      </div>
    </section>
  );
};

export default HeroSlider;
